export type FieldSectionRef = {
  handle: string
  name: string
}

export type FieldWithOptionalSection = {
  section?: FieldSectionRef | null
}

export type GroupedFieldsBySection<T extends FieldWithOptionalSection> = {
  unsectioned: T[]
  sections: Array<{
    handle: string
    name: string
    fields: T[]
  }>
}

/**
 * Split a flat field list into unsectioned fields + ordered sections.
 * Section order follows first appearance in the input list.
 */
export function groupFieldsBySection<T extends FieldWithOptionalSection>(
  fields: T[],
): GroupedFieldsBySection<T> {
  const unsectioned: T[] = []
  const sections: Array<{ handle: string; name: string; fields: T[] }> = []
  const sectionIndexByHandle = new Map<string, number>()

  for (const field of fields) {
    const section = field.section
    if (!section?.handle) {
      unsectioned.push(field)
      continue
    }

    const existingIndex = sectionIndexByHandle.get(section.handle)
    if (existingIndex === undefined) {
      sectionIndexByHandle.set(section.handle, sections.length)
      sections.push({
        handle: section.handle,
        name: section.name || section.handle,
        fields: [field],
      })
      continue
    }

    sections[existingIndex]?.fields.push(field)
  }

  return { unsectioned, sections }
}
